// =====================================================================
// Aba 7 — Templates de Mensagem (Fase 3, S38).
// CRUD clinic_message_templates. Required: template_key (slug),
// template_text. variables e gerenciada inline no TemplatePreview.
// =====================================================================
import React from "react";
import { Chip } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import AttachmentIcon from "@material-ui/icons/Attachment";

import CrudTable from "../components/CrudTable";
import CrudDialog from "../components/CrudDialog";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import TemplatePreview from "../components/TemplatePreview";
import useCrudResource from "../hooks/useCrudResource";

import {
  listTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate
} from "../../../services/clinicAdminService";

const MEDIA_LABEL = {
  video: "Vídeo",
  image: "Imagem",
  document: "Documento"
};

const COLUMNS = [
  {
    key: "template_key",
    label: "Chave",
    width: 180,
    render: (r) => <code>{r.template_key}</code>
  },
  { key: "template_name", label: "Nome", render: (r) => r.template_name || "—" },
  {
    key: "media_type",
    label: "Mídia",
    width: 110,
    render: (r) =>
      r.media_url ? (
        <Chip
          size="small"
          icon={<AttachmentIcon style={{ fontSize: 14 }} />}
          label={MEDIA_LABEL[r.media_type] || r.media_type || "anexo"}
          variant="outlined"
        />
      ) : (
        "—"
      )
  },
  {
    key: "version",
    label: "v",
    width: 60,
    align: "right",
    render: (r) => r.version ?? 1
  },
  {
    key: "is_active",
    label: "Ativo",
    width: 70,
    align: "center",
    render: (r) =>
      r.is_active ? (
        <CheckCircleIcon fontSize="small" htmlColor="#06c270" />
      ) : (
        "—"
      )
  }
];

const FIELDS = [
  {
    key: "template_key",
    label: "Chave (slug)",
    type: "text",
    required: true,
    colSpan: 6,
    regex: /^[a-z0-9_-]+$/i,
    regexError: "Apenas letras, números, _ e -",
    placeholder: "agendamento_confirmado",
    helperText: "Identificador único do template (max 64)."
  },
  {
    key: "template_name",
    label: "Nome (descritivo)",
    type: "text",
    colSpan: 6,
    placeholder: "Confirmação de agendamento"
  },
  // Custom: editor + preview, controla template_text E variables juntos.
  // O 4o arg `setField` (exposto pelo CrudDialog) permite setar variables
  // mesmo estando no render do template_text. Tudo no mesmo componente.
  {
    key: "template_text",
    label: "Conteúdo do template",
    type: "custom",
    required: true,
    render: (value, setValue, allValues, setField) => (
      <TemplatePreview
        value={value}
        onChange={setValue}
        variables={allValues?.variables || {}}
        onVariablesChange={(newVars) => setField("variables", newVars)}
      />
    )
  },
  // Hidden-ish: aparece como JSON cru pra debug. Auto-preenchido pelo
  // TemplatePreview acima. Transform converte string JSON → objeto antes
  // de mandar pro backend (workflow valida que e objeto JSON).
  {
    key: "variables",
    label: "Variáveis (JSON)",
    type: "textarea",
    rows: 3,
    helperText:
      "Auto-preenchido pelo editor acima. Pode editar manualmente em JSON se precisar.",
    transform: (v) => {
      if (v === "" || v == null) return null;
      if (typeof v === "object") return v;
      try {
        return JSON.parse(v);
      } catch {
        return null;
      }
    }
  },
  {
    key: "media_url",
    label: "URL da mídia (opcional)",
    type: "url",
    colSpan: 8,
    placeholder: "https://agenda.clinicaclee.com/midia/video.mp4"
  },
  {
    key: "media_type",
    label: "Tipo de mídia",
    type: "select",
    colSpan: 4,
    options: [
      { value: "video", label: "Vídeo" },
      { value: "image", label: "Imagem" },
      { value: "document", label: "Documento" }
    ]
  },
  { key: "is_active", label: "Ativo", type: "switch", colSpan: 6 },
  {
    key: "version",
    label: "Versão",
    type: "number",
    colSpan: 6,
    transform: (v) => (v === "" ? 1 : Number(v)),
    helperText: "Inteiro >= 1. Incremente ao editar templates já publicados."
  }
];

const TemplatesTab = ({ slug }) => {
  const crud = useCrudResource({
    slug,
    list: listTemplates,
    create: createTemplate,
    update: updateTemplate,
    remove: deleteTemplate,
    resourceLabel: "template"
  });

  // Quando initial vem do DB, `variables` pode vir como objeto.
  // Pra mostrar na textarea como JSON formatado, pre-stringify aqui.
  const prepareInitial = (row) => {
    if (!row) return null;
    const v = row.variables;
    return {
      ...row,
      variables:
        v && typeof v === "object" ? JSON.stringify(v, null, 2) : v || ""
    };
  };

  return (
    <>
      <CrudTable
        columns={COLUMNS}
        rows={crud.rows}
        loading={crud.loading}
        onAdd={crud.handleAdd}
        onEdit={(row) => crud.handleEdit(prepareInitial(row))}
        onDelete={crud.handleDelete}
        searchPlaceholder="Buscar template…"
        searchFields={["template_key", "template_name"]}
      />

      <CrudDialog
        open={crud.dialogOpen}
        onClose={crud.handleCloseDialog}
        onSave={crud.handleSave}
        title={crud.dialogInitial ? "Editar template" : "Novo template"}
        fields={FIELDS}
        initialValues={crud.dialogInitial}
        saving={crud.saving}
      />

      <ConfirmDeleteDialog
        open={!!crud.deleteTarget}
        onClose={crud.handleCancelDelete}
        onConfirm={crud.handleConfirmDelete}
        itemLabel={crud.deleteTarget?.template_name || crud.deleteTarget?.template_key}
        saving={crud.saving}
      />
    </>
  );
};

export default TemplatesTab;
